// AutoCompleteButton.js - React component for auto-completing tasks with undo functionality and user-based warehouse
import React from 'react';
import { autoCompleteOrder, undoAutoCompleteOrder, checkCanUndoAutoComplete, checkSendOutStatus } from '../../services/autoCompleteService.js';
import { toast } from 'react-toastify';
import { getCurrentUser } from '../../utils/auth';
import { getCurrentUserWarehouse, getLocationDisplayName, getUserLocation } from '../../utils/userLocationMapping.js';
import { getCurrentTimestamp } from '../../utils/dateUtils';
import '../styles/AutoCompleteButton.css';

const AutoCompleteButton = ({
  orderNo,
  variant = 'success',
  size = 'sm',
  showText = true,
  className="btn btn-primary rounded-pill shadow-sm mr-2",
  disabled = false,
  onAutoCompleteSuccess = null,
  onUndoSuccess = null,
  refreshOrderHistory = null // New prop for refreshing just the order history
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [canUndo, setCanUndo] = React.useState(false);
  const [lastCompletedAt, setLastCompletedAt] = React.useState(null);
  const [completedBy, setCompletedBy] = React.useState(null);
  const [userWarehouse, setUserWarehouse] = React.useState('Pomona');
  const [userLocation, setUserLocation] = React.useState('Pomona');
  const [showDeliveryModal, setShowDeliveryModal] = React.useState(false);
  const [deliveryMethod, setDeliveryMethod] = React.useState('');
  const [deliveryMethodError, setDeliveryMethodError] = React.useState('');

    // Check undo status on component mount and after successful operations
  React.useEffect(() => {
    if (orderNo) {
      checkUndoStatus();
      initializeUserInfo();
    }
  }, [orderNo]);

  if (!orderNo) {
    return null;
  }

  const initializeUserInfo = () => {
    try {
      const currentUser = getCurrentUser();
      const location = getUserLocation(); // Now reads from session, no parameter needed
      const warehouse = getCurrentUserWarehouse();

      setUserWarehouse(warehouse);
      setUserLocation(location);

      // console.log(`👤 User: ${currentUser} | 📍 Location: ${location} | 🏢 Warehouse: ${warehouse}`);
    } catch (error) {
      console.error('Error initializing user info:', error);
      setUserWarehouse('Pomona');
      setUserLocation('Pomona');
    }
  };

  const checkUndoStatus = async () => {
    try {
      const response = await checkCanUndoAutoComplete(orderNo);
      setCanUndo(response.canUndo);
      setLastCompletedAt(response.lastCompleted);
      setCompletedBy(response.completedBy);
    } catch (error) {
      console.error('Error checking undo status:', error);
      setCanUndo(false);
    }
  };

  const handleAutoComplete = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled || isLoading) return;

    // Pre-check: does this order have a Send Out appointment?
    try {
      const sendOutCheck = await checkSendOutStatus(orderNo);
      if (sendOutCheck.hasSendOutAppointment) {
        // Balance must be $0 to complete a Send Out order, except for exempt users
        const currentUser = getCurrentUser()?.toLowerCase();
        const balanceExemptUsers = ['ettiw', 'didip'];
        if (!balanceExemptUsers.includes(currentUser) && !sendOutCheck.isPaid) {
          toast.error(
            <div>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                ❌ Balance Due — Cannot Complete
              </div>
              <div style={{ fontSize: '0.9em' }}>
                This Send Out order has a remaining balance of ${(sendOutCheck.balanceDue ?? 0).toFixed(2)}.<br/>
                The balance must be $0.00 before completing.
              </div>
            </div>,
            { position: "top-right", autoClose: 6000, hideProgressBar: false, closeOnClick: true }
          );
          return;
        }
        // Show delivery method popup
        setShowDeliveryModal(true);
        return;
      }
    } catch {
      // If check fails, proceed normally without Send Out logic
    }

    await executeAutoComplete(null);
  };

  const handleDeliverySubmit = async () => {
    if (!deliveryMethod.trim() || deliveryMethod.trim().length < 8) {
      setDeliveryMethodError('Delivery method must be at least 8 characters.');
      return;
    }
    setDeliveryMethodError('');
    setShowDeliveryModal(false);
    await executeAutoComplete(deliveryMethod.trim());
    setDeliveryMethod('');
  };

  const executeAutoComplete = async (delivery) => {
    if (disabled || isLoading) return;

    setIsLoading(true);

    try {
      const currentUser = getCurrentUser();
      const warehouse = getCurrentUserWarehouse();
      const locationDisplay = getLocationDisplayName(userLocation);

      console.log(`🚀 Auto-completing order ${orderNo} by ${currentUser} in warehouse ${warehouse}`);

      // Use centralized API service
      const data = await autoCompleteOrder(orderNo, currentUser, delivery);

      console.log('✅ Auto-complete successful:', data);

      // Success toast notification with warehouse info
      toast.success(
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            ✅ Order {orderNo} Auto-Completed Successfully!
          </div>
          <div style={{ fontSize: '0.9em', lineHeight: '1.4' }}>
            • {data.tasksCompleted || 0} tasks marked as completed<br/>
            • Order status set to "Completed"<br/>
            • {data.isClientOrder ? 'Inventory warehouse updated (status kept — client order)' : 'Inventory updated to "Available"'}<br/>
            • Warehouse: {warehouse} ({locationDisplay})<br/>
            • Completed by: {currentUser}
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Update undo status
      await checkUndoStatus();

      // 🔥 FIXED: Use React state updates instead of hard refresh
      if (onAutoCompleteSuccess) {
        onAutoCompleteSuccess(orderNo, { ...data, warehouse });
      }

      // 🔥 FIXED: Refresh just the order history component if function provided
      if (refreshOrderHistory && typeof refreshOrderHistory === 'function') {
        console.log('🔄 Refreshing order history...');
        await refreshOrderHistory();
      }

      // Dispatch event for other components to listen (for broader updates)
      window.dispatchEvent(new CustomEvent('orderAutoCompleted', {
        detail: {
          orderNo,
          completedBy: currentUser,
          tasksCompleted: data.tasksCompleted,
          warehouse: warehouse,
          location: userLocation,
          completedAt: getCurrentTimestamp(),
          action: 'complete'
        }
      }));

    } catch (error) {
      console.error('❌ Auto-complete failed:', error);
      
      // Error toast notification
      toast.error(
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            ❌ Auto-Complete Failed
          </div>
          <div style={{ fontSize: '0.9em' }}>
            {error.message}<br/>
            <span style={{ opacity: 0.8 }}>Please try again or contact support.</span>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

    const handleUndoAutoComplete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled || isLoading) return;

    setIsLoading(true);

    try {
      const currentUser = getCurrentUser();
      const warehouse = getCurrentUserWarehouse();
      const locationDisplay = getLocationDisplayName(userLocation);
      
      console.log(`🔄 Undoing auto-complete for order ${orderNo} by ${currentUser}`);

      // Use centralized API service for undo
      const data = await undoAutoCompleteOrder(orderNo, currentUser);

      console.log('✅ Undo successful:', data);

      // Success toast notification with warehouse info
      toast.success(
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            🔄 Order {orderNo} Auto-Complete Undone!
          </div>
          <div style={{ fontSize: '0.9em', lineHeight: '1.4' }}>
            • Order restored to previous state<br/>
            • All tasks reverted to original status<br/>
            • Inventory status restored<br/>
            • Warehouse: {warehouse} ({locationDisplay})<br/>
            • Undone by: {currentUser}
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Update undo status
      await checkUndoStatus();

      // 🔥 FIXED: Use React state updates instead of hard refresh
      if (onAutoCompleteSuccess) {
        onAutoCompleteSuccess(orderNo, { ...data, warehouse });
      }

      if (onUndoSuccess) {
        onUndoSuccess(orderNo, { ...data, warehouse });
      }

      // 🔥 FIXED: Refresh just the order history component if function provided
      if (refreshOrderHistory && typeof refreshOrderHistory === 'function') {
        console.log('🔄 Refreshing order history after undo...');
        await refreshOrderHistory();
      }

      // Dispatch event for other components to listen
      window.dispatchEvent(new CustomEvent('orderAutoCompleted', {
        detail: {
          orderNo,
          completedBy: currentUser,
          tasksCompleted: data.tasksCompleted || 0,
          warehouse: warehouse,
          location: userLocation,
          completedAt: getCurrentTimestamp(),
          action: 'undo'
        }
      }));

    } catch (error) {
      console.error('❌ Undo failed:', error);
      
      // Error toast notification
      toast.error(
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            ❌ Undo Failed
          </div>
          <div style={{ fontSize: '0.9em' }}>
            {error.message}<br/>
            <span style={{ opacity: 0.8 }}>Please try again or contact support.</span>
          </div>
        </div>,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

const getButtonClass = () => {
    const baseClass = 'auto-complete-btn';
    const variantClass = canUndo ? 'auto-complete-btn-warning' : `auto-complete-btn-${variant}`;
    const sizeClass = `auto-complete-btn-${size}`;
    
    return `${baseClass} ${variantClass} ${sizeClass} ${className} ${disabled ? 'disabled' : ''}`.trim();
  };

  const getIcon = () => {
    if (isLoading) {
      return <i className="fas fa-spinner fa-spin"></i>;
    }
    
    if (canUndo) {
      return <i className="fas fa-undo"></i>;
    }
    
    return <i className="fas fa-check-double"></i>;
  };

  const getText = () => {
    if (!showText || variant === 'icon-only') return '';
    
    if (isLoading) {
      return canUndo ? 'Undoing...' : 'Completing...';
    }
    
    return canUndo ? 'Undo Auto Complete' : 'Auto Complete';
  };

  const getTitle = () => {
    const locationDisplay = getLocationDisplayName(userLocation);

    if (canUndo) {
      const completedInfo = [];
      if (lastCompletedAt) {
        completedInfo.push(`completed ${new Date(lastCompletedAt).toLocaleString()}`);
      }
      if (completedBy) {
        completedInfo.push(`by ${completedBy}`);
      }
      const completedText = completedInfo.length > 0 ? ` (${completedInfo.join(' ')})` : '';
      return `Undo auto-complete for order ${orderNo}${completedText} | Warehouse: ${userWarehouse} (${locationDisplay})`;
    }
    return `Auto-complete all tasks for order ${orderNo} | Warehouse: ${userWarehouse} (${locationDisplay})`;
  };

  const handleClick = canUndo ? handleUndoAutoComplete : handleAutoComplete;

  return (
    <>
      <button
        className={getButtonClass()}
        onClick={handleClick}
        disabled={disabled || isLoading}
        title={getTitle()}
        aria-label={getTitle()}
      >
        {getIcon()}
        {showText && <span className="ml-2">{getText()}</span>}
        {showText && (
          <small className="ml-1 opacity-75" style={{ fontSize: '0.75em' }}>
            ({userWarehouse})
          </small>
        )}
      </button>

      {/* Delivery Method Modal for Send Out appointments */}
      {showDeliveryModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: '#1e1e2e', border: '1px solid #444', borderRadius: '12px',
            padding: '28px', width: '420px', maxWidth: '90vw',
            display: 'flex', flexDirection: 'column', gap: '16px'
          }}>
            <h3 style={{ margin: 0, color: '#fff', fontSize: '16px' }}>
              📦 Confirm Send Out — Delivery Method
            </h3>
            <p style={{ margin: 0, color: '#aaa', fontSize: '13px' }}>
              This order is linked to a Send Out appointment. Enter the delivery method
              (carrier, tracking info, etc.) to confirm completion.
            </p>
            <textarea
              autoFocus
              rows={3}
              placeholder="e.g. UPS Ground — tracking #1Z999AA10123456784"
              value={deliveryMethod}
              onChange={e => { setDeliveryMethod(e.target.value); setDeliveryMethodError(''); }}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '10px',
                borderRadius: '6px', border: deliveryMethodError ? '1px solid #ef4444' : '1px solid #555',
                background: '#2a2a3e', color: '#fff', fontSize: '13px', resize: 'vertical'
              }}
            />
            {deliveryMethodError && (
              <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '-8px' }}>
                {deliveryMethodError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowDeliveryModal(false); setDeliveryMethod(''); setDeliveryMethodError(''); }}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: '1px solid #555',
                  background: 'transparent', color: '#aaa', cursor: 'pointer', fontSize: '13px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeliverySubmit}
                style={{
                  padding: '8px 16px', borderRadius: '6px', border: 'none',
                  background: '#10b981', color: '#fff', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '600'
                }}
              >
                Confirm &amp; Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AutoCompleteButton;